import { isAxiosError } from 'axios'
import { useContext, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import { toast } from 'react-toastify'

import { handleException } from 'clients/exception-handling'
import { AuthContext } from 'features/auth'
import { useProjectStore } from 'features/project/store'
import { useHubRole } from 'features/settings/features/access/hooks/use-hub-role'
import { addInFlightJob } from 'features/settings/features/access/lib/bulk-add-job-storage'
import type { AccessService } from 'features/settings/features/access/service'
import type {
  AddMemberPayload,
  ChildEditTarget,
  ChildProject,
  CreateUserPayload,
  EditTarget,
  MembersLoadError,
  TeamMember,
} from 'features/settings/features/access/types'
import { useCanAccess } from 'src/hooks/use-can-access'
import { ClientAuthPolicy } from 'src/types/auth-role'
import mixpanel from 'trackers/mixpanel'
import type { ProjectState } from 'types/project'

/**
 * Root of every team-members query key (flat + grouped variants).
 * Exported so out-of-page completion signals (the bulk-add job poller)
 * can invalidate the list when the async add lands.
 */
export const TEAM_MEMBERS_QUERY_KEY = 'team-members'

const QUERY_KEY = TEAM_MEMBERS_QUERY_KEY

export type RoleFilter = 'all' | 'admin' | 'viewer'

// 'forbidden' is the caller being refused by the members endpoint's
// authorizer (the legacy gate answers 401, newer ones 403); anything else
// is a generic failure.
export function classifyMembersError(error: unknown): MembersLoadError | null {
  if (!error) return null
  const status = isAxiosError(error) ? error.response?.status : undefined
  return status === 401 || status === 403 ? 'forbidden' : 'failed'
}

// The RBAC hub role names the member-management gates read (match the backend).
const RBAC_OWNER = 'owner'
const RBAC_ADMIN = 'admin'
const RBAC_MEMBER = 'member'

export type UseTeamMembersResult = {
  members: TeamMember[]
  isLoading: boolean
  membersError: MembersLoadError | null
  isMutating: boolean
  isUserAdmin: boolean
  isYogiAdmin: boolean
  isYogiUser: boolean
  canBulkAdd: boolean
  canGrantAdmin: boolean
  canResetUser: boolean
  canInvite: boolean
  currentUserEmail: string | undefined
  searchTerm: string
  setSearchTerm: (v: string) => void
  roleFilter: RoleFilter
  setRoleFilter: (v: RoleFilter) => void
  hideYogiStaff: boolean
  setHideYogiStaff: (v: boolean) => void
  showChildProjects: boolean
  setShowChildProjects: (v: boolean) => void
  childProjects: ChildProject[]
  childRoles: Record<string, Record<string, string | null>>
  isLoadingChildData: boolean
  parentProjectTitle: string
  childEditTarget: ChildEditTarget | null
  openChildEditModal: (target: ChildEditTarget) => void
  closeChildEditModal: () => void
  applyChildRoleEdit: (admin: boolean) => Promise<void>
  selectedEmails: string[]
  setSelectedEmails: (v: string[]) => void
  editTarget: EditTarget | null
  openEditModal: (target: EditTarget) => void
  closeEditModal: () => void
  applyRoleEdit: (admin: boolean) => Promise<void>
  isAddOpen: boolean
  openAdd: () => void
  closeAdd: () => void
  inviteExisting: (email: string, admin: boolean) => Promise<boolean>
  createAndInvite: (payload: CreateUserPayload) => Promise<void>
  removeMember: (email: string) => Promise<void>
  removeChildMember: (childProjectUuid: string, email: string) => Promise<void>
  removeSelectedMembers: () => Promise<void>
  resetUser: (email: string) => Promise<void>
}

export function useTeamMembers(
  projectId: string,
  service: AccessService,
): UseTeamMembersResult {
  const queryClient = useQueryClient()
  const { userDetails } = useContext(AuthContext)
  const currentUserEmail = userDetails?.email

  const parentProjectTitle =
    useProjectStore((s: ProjectState) => s.details?.title) ?? ''

  const isUserAdmin = useCanAccess(ClientAuthPolicy.ONLY_ADMIN_USERS)
  const isYogiAdmin = useCanAccess(ClientAuthPolicy.ONLY_YOGI_ADMINS)
  const isYogiUser = useCanAccess(ClientAuthPolicy.ONLY_YOGI_USERS)
  // The backend authorizes by scope; the UI shows an action to the hub roles
  // that hold it, read from GET /client/hub/user (fail-closed: hidden until
  // the role is known). Owner is reserved for Yogi staff, so anything a
  // customer manager should do is granted to hub admins as well.
  //  - adding someone as admin (hub_users:update): hub admins and owners
  //  - adding members, one or many (hub_users:create): every hub member
  const { hubRole } = useHubRole(projectId, service)
  const isHubAdminOrOwner = hubRole === RBAC_OWNER || hubRole === RBAC_ADMIN
  const canBulkAdd = isHubAdminOrOwner || hubRole === RBAC_MEMBER
  const canGrantAdmin = isHubAdminOrOwner
  // Reset-login is a destructive owner-only action. Gate on the canonical
  // effective-hub-role hook (respects an owner's "view as {role}" preview),
  // so an owner previewing as a lower role won't see the control.
  const canResetUser = hubRole === RBAC_OWNER
  const canInvite = useCanAccess(ClientAuthPolicy.ONLY_AUTHENTICATED_USERS)

  const [searchTerm, setSearchTerm] = useState('')
  const [roleFilter, setRoleFilter] = useState<RoleFilter>('all')
  const [hideYogiStaff, setHideYogiStaff] = useState(false)
  const [showChildProjects, setShowChildProjects] = useState(false)
  const [selectedEmails, setSelectedEmails] = useState<string[]>([])
  const [editTarget, setEditTarget] = useState<EditTarget | null>(null)
  const [childEditTarget, setChildEditTarget] =
    useState<ChildEditTarget | null>(null)
  const [isAddOpen, setAddOpen] = useState(false)

  const {
    data: rawMembers = [],
    isLoading,
    error: membersQueryError,
  } = useQuery(
    [QUERY_KEY, projectId, hideYogiStaff],
    () => service.fetchTeamMembers(projectId, !hideYogiStaff),
    { refetchOnWindowFocus: false },
  )

  const { data: groupedData, isLoading: isLoadingChildData } = useQuery(
    [QUERY_KEY, 'grouped', projectId, hideYogiStaff],
    () => service.fetchTeamMembersGrouped(projectId, !hideYogiStaff),
    {
      refetchOnWindowFocus: false,
      enabled: isYogiAdmin && showChildProjects,
    },
  )

  const childProjects = groupedData?.childProjects ?? []
  const childRoles = groupedData?.childRoles ?? {}

  const members = useMemo(() => {
    const term = searchTerm.toLowerCase()
    return rawMembers
      .filter((m) => {
        if (roleFilter !== 'all' && m.role !== roleFilter) return false
        if (!term) return true
        const fullName =
          `${m.first_name ?? ''} ${m.last_name ?? ''}`.toLowerCase()
        return fullName.includes(term) || m.email.toLowerCase().includes(term)
      })
      .sort((a, b) => {
        const nameA = `${a.first_name ?? ''} ${a.last_name ?? ''}`.trim()
        const nameB = `${b.first_name ?? ''} ${b.last_name ?? ''}`.trim()
        return nameA.localeCompare(nameB)
      })
  }, [rawMembers, searchTerm, roleFilter])

  const refetch = (): Promise<void> =>
    queryClient.invalidateQueries([QUERY_KEY, projectId])

  const { mutateAsync: doUpdateRole, isLoading: isUpdatingRole } = useMutation(
    ({ email, admin }: { email: string; admin: boolean }) =>
      service.updateMemberRole(projectId, email, admin),
    {
      meta: { operation: 'settings.access.use-team-members.useTeamMembers' },
      onSuccess: () => {
        refetch()
        mixpanel.track('access', { action: 'update_role' })
        toast.success('Role updated')
      },
      onError: (cause: unknown) => {
        handleException(cause, {
          operation: 'settings.access.use-team-members.useTeamMembers',
          disposition: 'delegated',
          owner: 'MutationCache',
        })

        toast.error('Failed to update role')
      },
    },
  )

  const { mutateAsync: doRemove, isLoading: isRemoving } = useMutation(
    (email: string) => service.removeMember(projectId, email),
    {
      meta: { operation: 'settings.access.use-team-members.useTeamMembers' },
      onSuccess: () => {
        refetch()
        mixpanel.track('access', { action: 'remove_member' })
        toast.success('Member removed')
      },
      onError: (cause: unknown) => {
        handleException(cause, {
          operation: 'settings.access.use-team-members.useTeamMembers',
          disposition: 'delegated',
          owner: 'MutationCache',
        })

        toast.error('Failed to remove member')
      },
    },
  )

  const { mutateAsync: doResetUser, isLoading: isResettingUser } = useMutation(
    (email: string) => service.resetUser(projectId, email),
    {
      meta: { operation: 'settings.access.use-team-members.useTeamMembers' },
      onSuccess: (result) => {
        if (result.success) {
          mixpanel.track('access', { action: 'reset_user' })
          toast.success(
            result.msg || 'Login reset — a setup email has been sent',
          )
        } else {
          toast.error(result.msg || 'Failed to reset login')
        }
      },
      onError: (error: unknown) => {
        handleException(error, {
          operation: 'settings.access.use-team-members.useTeamMembers',
          disposition: 'delegated',
          owner: 'MutationCache',
        })

        const serverMsg =
          isAxiosError(error) &&
          typeof error.response?.data === 'object' &&
          error.response?.data !== null
            ? (error.response.data as { msg?: string }).msg
            : undefined
        toast.error(serverMsg ?? 'Failed to reset login')
      },
    },
  )

  const { mutateAsync: doAddMember, isLoading: isAddingMember } = useMutation(
    (payload: AddMemberPayload) =>
      service.addMember(projectId, parentProjectTitle, payload),
    {
      meta: { operation: 'settings.access.use-team-members.useTeamMembers' },
      onSuccess: (dispatched, payload) => {
        // The add runs as a job; the app-shell poller toasts the outcome and
        // refreshes this list when it finishes.
        addInFlightJob({
          jobId: dispatched.jobId,
          hubUuid: projectId,
          hubTitle: parentProjectTitle,
          startedAt: Date.now(),
        })
        toast.success(
          `Adding ${payload.email}. We'll let you know when it's done.`,
        )
      },
      onError: (error: unknown, payload) => {
        handleException(error, {
          operation: 'settings.access.use-team-members.useTeamMembers',
          disposition: 'delegated',
          owner: 'MutationCache',
        })

        toast.error(
          payload.admin && isAxiosError(error) && error.response?.status === 403
            ? 'Only hub admins can add someone as an admin'
            : 'Failed to add member',
        )
      },
    },
  )

  const { mutateAsync: doUpdateChildRole, isLoading: isUpdatingChildRole } =
    useMutation(
      ({
        childProjectUuid,
        email,
        admin,
        hasExistingRow,
      }: {
        childProjectUuid: string
        email: string
        admin: boolean
        hasExistingRow: boolean
      }) =>
        service.shareOrUpdateMemberRole(
          childProjectUuid,
          email,
          admin,
          hasExistingRow,
        ),
      {
        meta: { operation: 'settings.access.use-team-members.useTeamMembers' },
        onSuccess: () => {
          queryClient.invalidateQueries([QUERY_KEY, 'grouped', projectId])
          toast.success('Role updated')
        },
        onError: (cause: unknown) => {
          handleException(cause, {
            operation: 'settings.access.use-team-members.useTeamMembers',
            disposition: 'delegated',
            owner: 'MutationCache',
          })

          toast.error('Failed to update role')
        },
      },
    )

  const { mutateAsync: doRemoveChild, isLoading: isRemovingChild } =
    useMutation(
      ({
        childProjectUuid,
        email,
      }: {
        childProjectUuid: string
        email: string
      }) => service.removeMember(childProjectUuid, email),
      {
        meta: { operation: 'settings.access.use-team-members.useTeamMembers' },
        onSuccess: () => {
          queryClient.invalidateQueries([QUERY_KEY, 'grouped', projectId])
          mixpanel.track('access', { action: 'remove_child_member' })
          toast.success('Member removed from child hub')
        },
        onError: (cause: unknown) => {
          handleException(cause, {
            operation: 'settings.access.use-team-members.useTeamMembers',
            disposition: 'delegated',
            owner: 'MutationCache',
          })

          toast.error('Failed to remove member from child hub')
        },
      },
    )

  const isMutating =
    isUpdatingRole ||
    isRemoving ||
    isAddingMember ||
    isUpdatingChildRole ||
    isRemovingChild ||
    isResettingUser

  const openEditModal = (target: EditTarget): void => setEditTarget(target)
  const closeEditModal = (): void => setEditTarget(null)

  const openChildEditModal = (target: ChildEditTarget): void =>
    setChildEditTarget(target)
  const closeChildEditModal = (): void => setChildEditTarget(null)

  const applyChildRoleEdit = async (admin: boolean): Promise<void> => {
    if (!childEditTarget) return
    await doUpdateChildRole({
      childProjectUuid: childEditTarget.childProjectUuid,
      email: childEditTarget.email,
      admin,
      hasExistingRow: childEditTarget.currentRole !== null,
    })
    setChildEditTarget(null)
  }

  const applyRoleEdit = async (admin: boolean): Promise<void> => {
    if (!editTarget) return
    const keys =
      editTarget.kind === 'single' ? [editTarget.email] : editTarget.emails

    const parentEmails = keys.filter((k) => !k.includes('::'))
    const childKeys = keys
      .filter((k) => k.includes('::'))
      .map((k) => {
        const sep = k.indexOf('::')
        return { email: k.slice(0, sep), childProjectUuid: k.slice(sep + 2) }
      })

    await Promise.all([
      ...parentEmails.map((email) => doUpdateRole({ email, admin })),
      ...childKeys.map(({ email, childProjectUuid }) =>
        doUpdateChildRole({
          childProjectUuid,
          email,
          admin,
          hasExistingRow: childRoles[email]?.[childProjectUuid] != null,
        }),
      ),
    ])
    setSelectedEmails([])
    closeEditModal()
  }

  const removeMember = async (email: string): Promise<void> => {
    await doRemove(email)
    setSelectedEmails((prev) => prev.filter((e) => e !== email))
  }

  // Bulk remove for the selection — mirrors applyRoleEdit's key handling
  // (`email` for parent rows, `email::childUuid` for child rows). Calls
  // the service directly (not the single-remove mutations) so N removals
  // produce ONE summary toast + one refetch instead of N of each.
  const removeSelectedMembers = async (): Promise<void> => {
    const keys = selectedEmails
    const parentEmails = keys.filter((k) => !k.includes('::'))
    const childKeys = keys
      .filter((k) => k.includes('::'))
      .map((k) => {
        const sep = k.indexOf('::')
        return { email: k.slice(0, sep), childProjectUuid: k.slice(sep + 2) }
      })
    const results = await Promise.allSettled([
      ...parentEmails.map((email) => service.removeMember(projectId, email)),
      ...childKeys.map(({ email, childProjectUuid }) =>
        service.removeMember(childProjectUuid, email),
      ),
    ])
    const failed = results.filter((r) => r.status === 'rejected').length
    const removed = results.length - failed
    refetch()
    queryClient.invalidateQueries([QUERY_KEY, 'grouped', projectId])
    mixpanel.track('access', { action: 'bulk_remove_members' })
    if (failed === 0) {
      toast.success(`${removed} member${removed === 1 ? '' : 's'} removed`)
    } else {
      toast.error(
        `${removed} removed, ${failed} failed — refresh and retry the rest`,
      )
    }
    setSelectedEmails([])
  }

  const removeChildMember = async (
    childProjectUuid: string,
    email: string,
  ): Promise<void> => {
    await doRemoveChild({ childProjectUuid, email })
  }

  const inviteExisting = async (
    email: string,
    admin: boolean,
  ): Promise<boolean> => {
    // An unknown address goes to the modal's create step for names first.
    if (!(await service.checkUserExists(email))) return false
    await doAddMember({ email, admin })
    mixpanel.track('access', { action: 'invite_existing' })
    return true
  }

  const createAndInvite = async (payload: CreateUserPayload): Promise<void> => {
    await doAddMember(payload)
    mixpanel.track('access', { action: 'create_and_invite' })
  }

  const resetUser = async (email: string): Promise<void> => {
    await doResetUser(email)
  }

  return {
    members,
    isLoading,
    membersError: classifyMembersError(membersQueryError),
    isMutating,
    isUserAdmin,
    isYogiAdmin,
    isYogiUser,
    canBulkAdd,
    canGrantAdmin,
    canResetUser,
    canInvite,
    currentUserEmail,
    searchTerm,
    setSearchTerm,
    roleFilter,
    setRoleFilter,
    hideYogiStaff,
    setHideYogiStaff,
    showChildProjects,
    setShowChildProjects,
    childProjects,
    childRoles,
    isLoadingChildData,
    parentProjectTitle,
    childEditTarget,
    openChildEditModal,
    closeChildEditModal,
    applyChildRoleEdit,
    selectedEmails,
    setSelectedEmails,
    editTarget,
    openEditModal,
    closeEditModal,
    applyRoleEdit,
    isAddOpen,
    openAdd: () => setAddOpen(true),
    closeAdd: () => setAddOpen(false),
    inviteExisting,
    createAndInvite,
    removeMember,
    removeChildMember,
    removeSelectedMembers,
    resetUser,
  }
}
